import React, { useState } from "react";
import { Table, Button } from "react-bootstrap";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { IconArrowDown, IconArrowUp, IconMinus, IconPlus } from "@tabler/icons-react";
import { ReportRoomResponseV4, ReportRoomDataV4 } from "../../../../services/cm-report/response/ReportRoomResponseV4";
import { formatPrice } from "../../../../shared/utils/formatPrice";

type SortDirection = "asc" | "desc";
type OrderByOption = "room_type" | "percentage" | "room_nights" | "reservations" | "avg_nights" | "booking_window" | "revenue" | "ota_com" | "avg_rate" | "cancel_count";

interface RoomReportTableV4Props {
    data: ReportRoomResponseV4;
    sortConfig: { orderBy: string; sort: SortDirection };
    onSort: (orderBy: string) => void;
}

type ParentColumnKey = Exclude<keyof ReportRoomDataV4, 'children'>;

const RoomReportTableV4: React.FC<RoomReportTableV4Props> = ({ data, sortConfig, onSort }) => {
    const [expandedRow, setExpandedRow] = useState<number | null>(null);

    const parentColumns: { label: string; key: ParentColumnKey, sortKey?: OrderByOption }[] = [
        { label: "Room Type", key: "room_type", sortKey: "room_type" },
        { label: "Percentage", key: "percentage", sortKey: "percentage" },
        { label: "Room Night Sold", key: "room_nights", sortKey: "room_nights" },
        { label: "Reservation", key: "reservations", sortKey: "reservations" },
        { label: "Avg. Nights", key: "avg_nights", sortKey: "avg_nights" },
        { label: "Avg. Booking Window", key: "booking_window", sortKey: "booking_window" },
        { label: "Revenue (IDR)", key: "revenue", sortKey: "revenue" },
        { label: "Avg. Rate (IDR)", key: "avg_rate", sortKey: "avg_rate" },
        { label: "Cancellation Count", key: "cancel_count", sortKey: "cancel_count" },
        { label: "OTA Commissions", key: "ota_com", sortKey: "ota_com" }
    ];

    const childColumns = [
        { label: "Agent Name", key: "agent_name" },
        { label: "Room Night Sold", key: "room_nights" },
        { label: "Reservation", key: "reservations" },
        { label: "Avg. Nights", key: "avg_nights" },
        { label: "Avg. Booking Window", key: "booking_window" },
        { label: "Revenue (IDR)", key: "revenue" },
        { label: "Avg. Rate (IDR)", key: "avg_rate" },
        { label: "Cancellation Count", key: "cancel_count" },
        { label: "OTA Commissions", key: "ota_com" }
    ];

    const totalRow = data.total;

    const getSortIcon = (columnKey: OrderByOption) => {
        if (sortConfig.orderBy !== columnKey) {
            return null;
        }
        return sortConfig.sort === 'asc' ? <IconArrowUp size={14} /> : <IconArrowDown size={14} />;
    };

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Report Room V4 Table", 14, 10);

        const tableData = data.data.map(row =>
            parentColumns.map(col => {
                const value = row[col.key];
                if (col.key === 'revenue' || col.key === 'avg_rate') return formatPrice(Number(value));
                if (col.key === 'percentage') return `${value}%`;
                return value ?? "N/A";
            })
        );

        const tableTotal = parentColumns.map(col => {
            if (col.key === 'room_type') return 'Total';
            const key = col.key as keyof typeof totalRow;
            if (key === 'revenue' || key === 'avg_rate') return formatPrice(Number(totalRow?.[key]));
            return totalRow?.[key] ?? "N/A";
        });

        autoTable(doc, {
            head: [parentColumns.map(col => col.label)],
            body: [...tableData, tableTotal as any],
        });

        doc.save("ReportRoomTableV4.pdf");
    };

    const exportToExcel = () => {
        const wsData = [
            parentColumns.map(col => col.label),
            ...data.data.map(row =>
                parentColumns.map(col => {
                    const value = row[col.key];
                    if (col.key === 'revenue' || col.key === 'avg_rate') return formatPrice(Number(value));
                    if (col.key === 'percentage') return `${value}%`;
                    return value ?? "N/A";
                })
            ),
            parentColumns.map(col => {
                if (col.key === 'room_type') return 'Total';
                const key = col.key as keyof typeof totalRow;
                if (key === 'revenue' || key === 'avg_rate') return formatPrice(Number(totalRow?.[key]));
                if (col.key === 'percentage') return '-';
                return totalRow?.[key] ?? "N/A";
            })
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report Room V4");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(dataBlob, "ReportRoomTableV4.xlsx");
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>

            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            {parentColumns.map((col) => (
                                <th
                                    key={col.key}
                                    onClick={() => col.sortKey && onSort(col.sortKey)}
                                    style={{ cursor: col.sortKey ? 'pointer' : 'default' }}
                                >
                                    <div className="d-flex align-items-center justify-content-between">
                                        {col.label}
                                        {col.sortKey && getSortIcon(col.sortKey as OrderByOption)}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data?.data.length > 0 ? (
                            data.data.map((row, rowIndex) => (
                                <React.Fragment key={rowIndex}>
                                    <tr>
                                        {parentColumns.map((col) => (
                                            <td key={col.key}>
                                                {col.key === "room_type" ? (
                                                    <div className="d-flex align-items-center">
                                                        <button
                                                            className="btn btn-primary rounded-circle d-flex align-items-center justify-content-center p-0 me-2"
                                                            style={{ width: "24px", height: "24px" }}
                                                            onClick={(e) => {
                                                                e.stopPropagation(); // Mencegah sort saat klik expand
                                                                setExpandedRow(rowIndex === expandedRow ? null : rowIndex);
                                                            }}
                                                        >
                                                            {rowIndex === expandedRow ? <IconMinus size={14} /> : <IconPlus size={14} />}
                                                        </button>
                                                        {row[col.key]}
                                                    </div>
                                                ) : col.key === 'revenue' || col.key === 'avg_rate' || col.key === 'ota_com' ? (
                                                    formatPrice(Number(row[col.key]))
                                                ) : col.key === 'percentage' ? (
                                                    `${row[col.key]}%`
                                                ) : (
                                                    row[col.key]
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                    {expandedRow === rowIndex && (
                                        <tr>
                                            <td colSpan={parentColumns.length} className="p-0">
                                                <Table hover className="mb-0">
                                                    <thead className="table-secondary">
                                                        <tr>
                                                            <th style={{ width: "48px" }}></th>
                                                            {childColumns.map(c => <th key={c.key}>{c.label}</th>)}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {row.children.map((child, childIndex) => (
                                                            <tr key={childIndex}>
                                                                <td></td>
                                                                {childColumns.map(c => (
                                                                    <td key={c.key}>
                                                                        {c.key === 'revenue' || c.key === 'avg_rate' || c.key === 'ota_com'
                                                                            ? formatPrice(Number(child[c.key as keyof typeof child]))
                                                                            : child[c.key as keyof typeof child]}
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={parentColumns.length} className="text-center">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td><strong>Total</strong></td>
                            <td><strong>{`${totalRow?.percentage}%`}</strong></td>
                            <td><strong>{totalRow?.room_nights}</strong></td>
                            <td><strong>{totalRow?.reservations}</strong></td>
                            <td><strong>{totalRow?.avg_nights}</strong></td>
                            <td><strong>{totalRow?.booking_window}</strong></td>
                            <td><strong>{formatPrice(Number(totalRow?.revenue))}</strong></td>
                            <td><strong>{formatPrice(Number(totalRow?.avg_rate))}</strong></td>
                            <td><strong>{totalRow?.cancel_count}</strong></td>
                            <td><strong>{formatPrice(Number(totalRow?.ota_com))}</strong></td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
};

export default RoomReportTableV4;